import React, { memo, useEffect, useMemo } from 'react'
import { CircularProgress } from '@material-ui/core'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import api from '../../../../services/api'
import messages from '../../../../services/messages'

import {
  LibraryBooks,
  ChevronLeftRounded as ArrowLeftIcon,
} from '@material-ui/icons'

import {
  getScript as getApiScript,
  getScriptError,
  setScript,
  setScriptDescription,
} from '../../../../redux/script/actions'

import {
  getScript,
  favoritedScript,
  favoriteError,
} from '../../../../redux/favoriteScript/actions'

import useStyles from './style'
import Typography from '../../../atoms/display/typography'
import Icon from '../../../atoms/display/icon'
import { removeSpecials } from '../../../../utils/customMethods'
import ScriptCategoryItem from './subs/ScriptCategoryItem'

function ScriptsComponent({ keyword, ...props }) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const script = useSelector((state) => state.script)
  const { favorites, status } = useSelector((state) => state.favoriteScripts)
  const activeConversation = useSelector((state) => state.activeConversation)
  const conversation = useSelector(
    (state) => state.conversations.conversations[activeConversation]
  )
  const enterDescription = useSelector(
    (state) => state.script.description?.[activeConversation]
  )

  const [triggerExpanded, setTriggerExpanded] = React.useState(false)

  const getScriptsFavorites = async () => {
    dispatch(getScript())
    try {
      const response = await messages.get(`/tech4h-messages/script/favorite`)

      if (response.status === 200) {
        dispatch(
          favoritedScript({
            favoriteScript: response.data.data.favorite_messages,
            scriptId: null,
          })
        )
      } else {
        throw response.data
      }
    } catch (error) {
      const message =
        error?.response?.status === 500
          ? 'Unavailable service'
          : error?.response?.data?.message
      enqueueSnackbar(t('toasts.actionError'), {
        variant: 'error',
      })
      dispatch(favoriteError({ action: 'get_script', message: message }))
    }
  }

  const getScriptFunction = async (skillId) => {
    if (!skillId) return

    dispatch(getApiScript(skillId))

    try {
      const response = await api.get(
        `/scripts${skillId ? `?skill_id=${skillId}` : ''}`
      )
      if (response.status === 200) {
        dispatch(setScript(skillId, response.data?.data?.scripts))
      } else {
        throw response.data
      }
    } catch (error) {
      let message =
        error?.response?.status === 500
          ? 'Unavailable service'
          : error?.response?.data?.message
      dispatch(getScriptError(skillId, message))
    }
  }

  const scriptContent = useMemo(() => {
    const scriptItems =
      script?.content?.[conversation?.skillId]
        ?.filter((el) => el?.activated)
        ?.map((el) => {
          const isFavorite = favorites?.includes(el.id)
          const categories = el?.categories || []
          return {
            ...el,
            categories: isFavorite
              ? [...categories, t('commons.favorites')]
              : el?.categories,
          }
        }) || []

    if (keyword) {
      return (
        scriptItems?.filter((el) => {
          const { title, text, categories } = el
          const search = keyword.toLowerCase()
          return (
            removeSpecials(title?.toLowerCase()).includes(search) ||
            removeSpecials(text?.toLowerCase()).includes(search) ||
            categories?.some((category) =>
              removeSpecials(category?.toLowerCase()).includes(search)
            )
          )
        }) || []
      )
    }

    return scriptItems
  }, [
    script?.content?.[conversation?.skillId],
    favorites,
    script?.status?.get_script?.[conversation?.skillId],
  ])

  const scriptCategories = useMemo(() => {
    const hasCategoryNull = scriptContent.some(
      (item) => item.categories === null
    )
    let category = []
    const hasItemFavorite = scriptContent.some((item) =>
      item.categories?.includes(t('commons.favorites'))
    )
    scriptContent.forEach((item) => {
      category = category.concat(item.categories || [])
    })

    hasCategoryNull && category.unshift(t('faqs.category.notfound'))
    hasItemFavorite && category.unshift(t('commons.favorites'))

    return [...new Set(category)] || []
  }, [scriptContent])

  const renderDescriptionScript = () => {
    return (
      <div
        className={classes.descriptionFatherContainer}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <div className={classes.descriptionContainer}>
          <div
            className={classes.iconContainer}
            style={{ width: 48, height: 48 }}
            onClick={() => {
              dispatch(setScriptDescription(activeConversation, null))
            }}
          >
            <ArrowLeftIcon />
          </div>

          <h1 className={classes.descriptionScriptText}>
            {enterDescription?.title}
          </h1>
        </div>
        <hr className={classes.scriptDivider} style={{ opacity: 0.5 }}></hr>
        <Typography className={classes.descriptionText}>
          {enterDescription?.text}
        </Typography>
      </div>
    )
  }

  useEffect(() => {
    if (keyword) {
      setTriggerExpanded(true)
    }
    return () => {
      setTriggerExpanded(false)
    }
  }, [keyword, scriptCategories])

  const renderScript = useMemo(() => {
    if (!scriptCategories?.length) {
      return (
        <div style={{ textAlign: 'center', marginTop: '1em' }}>
          <Typography>{t('commons.searchResultEmpty')}</Typography>
        </div>
      )
    }
    if (enterDescription) {
      return <>{renderDescriptionScript()}</>
    } else {
      return scriptCategories?.map((category, key) => {
        return (
          <ScriptCategoryItem
            key={key}
            category={category}
            scriptContent={scriptContent}
            keyword={keyword}
            triggerExpanded={triggerExpanded}
          />
        )
      })
    }
  }, [scriptCategories, enterDescription])

  useEffect(() => {
    const skillId = conversation?.skillId

    if (!skillId) return
    if (script?.status?.get_script?.[skillId]?.loading) return
    if (script?.status?.get_script?.[skillId]?.success) return

    getScriptFunction(skillId)
  }, [conversation?.skillId, script])

  useEffect(() => {
    if (status?.loading) return
    if (status?.success) return

    getScriptsFavorites()
  }, [])

  return (
    <>
      <div className={classes.titleContainer}>
        <Icon text={t('buttons.script')}>
          {script?.status?.get_script?.[conversation?.skillId]?.loading ? (
            <CircularProgress size={24} />
          ) : (
            <LibraryBooks />
          )}
        </Icon>
      </div>

      <div style={{ marginTop: '10px' }}>
        <>{renderScript}</>
      </div>
    </>
  )
}

export default memo(ScriptsComponent)
